<template>
  <div class="settings-page">
    <h2>Workspaces</h2>
    <div class="subtitle">
      Manage and create workspaces for your organization.
    </div>

    <BaseAlert
      v-if="!belongsToAnyWorkspace && (workspaces?.length ?? 0) > 0"
      state="info"
    >
      You are not a member of any workspace in this organization.
    </BaseAlert>

    <div class="workspace-list">
      <div v-for="ws in workspaces" :key="ws.id" class="workspace-row">
        <button
          class="workspace-info workspace-button"
          @click="navigateToWorkspace(ws.id)"
        >
          <div class="avatar placeholder">
            {{ getInitials(ws.name || "") }}
          </div>
          <div class="details">
            <div
              class="name"
              :class="{
                'name--disabled':
                  !isEntitledToWorkspaces && ws.id !== defaultWorkspaceId,
              }"
            >
              {{ ws.name }}
              <span v-if="ws.id === defaultWorkspaceId" class="default-tag"
                >Default</span
              >
            </div>
            <div
              class="members"
              :class="{
                'members--disabled':
                  !isEntitledToWorkspaces && ws.id !== defaultWorkspaceId,
              }"
            >
              {{ workspaceMembers?.[ws.id]?.length || 0 }} members
            </div>
          </div>
        </button>
        <div class="status">
          <BaseButton
            kind="auxiliary"
            text="Open"
            @click="openWorkspace(ws.id, $event)"
          >
            <template #icon>
              <OpenIcon />
            </template>
          </BaseButton>
          <ButtonMenu
            :offset="[0, 4]"
            placement="bottom-end"
            v-if="
              (ws.id === defaultWorkspaceId || isEntitledToWorkspaces) &&
              canRenameWorkspace(ws.id)
            "
          >
            <template #btn>
              <BaseButton kind="auxiliary">
                <template #icon>
                  <MoreIcon />
                </template>
              </BaseButton>
            </template>
            <template #content>
              <BaseButton
                kind="auxiliary"
                text="Rename"
                is-expanded
                :state="canRenameWorkspace(ws.id) ? 'default' : 'disabled'"
                :tooltip="
                  canRenameWorkspace(ws.id)
                    ? undefined
                    : 'You are not allowed to perform this action.'
                "
                :tooltip-display="canRenameWorkspace(ws.id) ? 'none' : 'always'"
                @click="renameWorkspace(ws.id)"
              >
                <template #icon>
                  <EditIcon />
                </template>
              </BaseButton>
              <BaseButton
                v-if="
                  ws.id !== defaultWorkspaceId &&
                  workspaces &&
                  workspaces.length > 1
                "
                kind="auxiliary"
                text="Delete"
                is-expanded
                @click="deleteWorkspace(ws.id)"
              >
                <template #icon>
                  <DeleteIcon />
                </template>
              </BaseButton>
            </template>
          </ButtonMenu>
        </div>
      </div>
    </div>

    <div v-if="canCreateWorkspace" class="create-section">
      <h3>Create workspace</h3>
      <p class="info-text">
        Create a new workspace to organize your team's work and keep its content
        organized and separate.
      </p>

      <div class="create-input">
        <FormInput
          v-model:value="workspaceName"
          type="text"
          placeholder="Workspace name"
          :state="errorInName ? 'error' : 'default'"
          @keydown.enter="handleCreateWorkspace"
        />
        <UpgradeButton
          entitlement-key="workspaces"
          :organization-id="orgId"
          kind="main"
          type="primary"
          text="Create"
          :state="isEntitledToWorkspaces ? 'default' : 'disabled'"
          :tooltip="
            canCreateWorkspace
              ? undefined
              : 'You are not allowed to perform this action.'
          "
          :tooltip-display="canCreateWorkspace ? 'none' : 'always'"
          @click="handleCreateWorkspace"
        />
      </div>
      <BaseAlert v-if="errorInName" state="error">
        Workspace name cannot be empty.
      </BaseAlert>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, toRefs } from "vue";

import { AccountId, FullWorkspace, WorkspaceId } from "@ctrl/controller-rpc";

import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import UpgradeButton from "@/components/base/UpgradeButton.vue";
import ButtonMenu from "@/components/elements/ButtonMenu.vue";
import FormInput from "@/components/graph/definition/form/FormInput.vue";
import DeleteIcon from "@/components/icons/DeleteIcon.vue";
import EditIcon from "@/components/icons/EditIcon.vue";
import MoreIcon from "@/components/icons/MoreIcon.vue";
import OpenIcon from "@/components/icons/OpenIcon.vue";
import * as inject from "@/components/inject";
import { openUpgradeModal } from "@/components/modals/upgrade";
import {
  createWorkspace,
  getOrganizationDefaultWorkspace,
  openDeleteWorkspaceModal,
  openRenameWorkspaceModal,
  sortOrganizationWorkspaces,
} from "@/components/workspaces";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import { RouteNames } from "@/routes/types";
import { navigateTo } from "@/utils/navigate";

interface Workspace {
  id: WorkspaceId;
  name: string;
}

export default defineComponent({
  name: "Workspaces",
  components: {
    BaseAlert,
    BaseButton,
    UpgradeButton,
    ButtonMenu,
    EditIcon,
    FormInput,
    MoreIcon,
    OpenIcon,
    DeleteIcon,
  },
  props: {
    orgId: {
      type: String,
      required: true,
    },
  },
  async setup(props) {
    const { orgId } = toRefs(props);
    const controller = inject.controller();
    const router = inject.router();
    const billing = inject.billing();
    const profile = inject.profile();
    const workspaceName = ref("");
    const errorInName = ref(false);
    const showMoreOptions = ref(true);

    // billing is async-initialized; keep entitlement reactive as it becomes available
    const workspacesEntitlementRef = computed(() => {
      return billing.value?.isEntitledTo("workspaces", orgId.value);
    });
    const isEntitledToWorkspaces = computed(() => {
      return workspacesEntitlementRef.value?.value ?? false;
    });

    const getInitials = (name: string) => {
      return name
        .split(" ")
        .map((word) => word[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    };

    // Set up reactive workspace data with natural dependencies
    const releaser = useReleaser();
    const [organization, workspaces, workspaceMembers] = await (async () => {
      const orgRef = releaser.add(
        bindAsync(orgId, async (id) =>
          id ? controller.organization.get(id) : releasableRef(null)
        )
      );
      const workspaceIds = releaser.add(
        bindAsync(orgId, async (id) =>
          id ? controller.workspace.all() : releasableRef([])
        )
      );

      const workspaces = releaser.add(
        workspaceIds.then((ids) =>
          bindAsync(ids, async (ids) => {
            if (!ids?.length) return releasableRef<FullWorkspace[]>([]);

            const wsPromises = ids.map(async (id: string) => {
              const wsRef = await controller.workspace.get(id);
              const ws = wsRef.value;
              wsRef.release();
              if (!ws || ws.owner !== orgId.value) return null;
              return ws;
            });

            const workspaces = (await Promise.all(wsPromises))
              .filter(
                (ws: FullWorkspace | null): ws is FullWorkspace => ws !== null
              )
              .toSorted(sortOrganizationWorkspaces);

            return releasableRef(workspaces);
          })
        )
      );

      const workspaceMembers = releaser.add(
        workspaces.then((workspaces) =>
          bindAsync(workspaces, async (workspaces) => {
            if (!workspaces?.length)
              return releasableRef<Record<string, AccountId[]>>({});

            const membersPromises = workspaces.map(async (ws: Workspace) => {
              const membersRef = await controller.workspace.member.all(ws.id);
              const members = membersRef.value;
              membersRef.release();
              return { id: ws.id, members };
            });

            const loadedMembers = await Promise.all(membersPromises);
            const membersRecord: Record<string, AccountId[]> = {};
            loadedMembers.forEach(
              ({ id, members }: { id: string; members: AccountId[] }) => {
                membersRecord[id] = members;
              }
            );
            return releasableRef(membersRecord);
          })
        )
      );

      return Promise.all([orgRef, workspaces, workspaceMembers]);
    })();

    const addWorkspace = async () => {
      if (!workspaceName.value.trim()) {
        errorInName.value = true;
        return;
      }

      errorInName.value = false;
      const newWorkspaceName = workspaceName.value;

      await createWorkspace(controller, orgId.value, newWorkspaceName, true);

      workspaceName.value = "";
    };

    const handleCreateWorkspace = () => {
      if (!isEntitledToWorkspaces.value) {
        openUpgradeModal(billing, orgId.value, "workspaces");
        return;
      }
      addWorkspace();
    };

    const navigateToWorkspace = async (workspaceId: WorkspaceId) => {
      await navigateTo(RouteNames.SettingsWorkspace, router, {
        params: { orgId: orgId.value, workspaceId },
      });
    };

    const openWorkspace = (workspaceId: WorkspaceId, event?: MouseEvent) => {
      void navigateTo(
        RouteNames.Workspace,
        router,
        {
          params: { orgId: orgId.value, workspaceId },
        },
        event
      );
    };

    const renameWorkspace = async (id: string) => {
      // Find the workspace to get its current name
      const workspace = workspaces.value?.find((ws) => ws.id === id);
      if (!workspace) return;

      openRenameWorkspaceModal(id, workspace.name, controller);
    };

    const deleteWorkspace = async (id: string) => {
      if (workspaces.value && workspaces.value.length <= 1) {
        controller.notifications.notify(
          "Cannot delete the last workspace. Each organization must have at least one workspace."
        );
        return;
      }

      // Find the workspace to get its name
      const workspace = workspaces.value?.find((ws) => ws.id === id);
      if (!workspace) return;

      openDeleteWorkspaceModal(id, workspace.name, controller);
    };

    const canCreateWorkspace = computed(() => {
      return (
        organization.value?.permissions?.includes("workspace-create") ?? false
      );
    });

    const canRenameWorkspace = (workspaceId: string): boolean => {
      const workspace = workspaces.value?.find((ws) => ws.id === workspaceId);
      return workspace?.permissions?.includes("rename") ?? false;
    };

    const defaultWorkspaceId = await getOrganizationDefaultWorkspace(
      controller,
      orgId.value
    );

    const belongsToAnyWorkspace = computed(() => {
      const wsList = workspaces.value;
      const members = workspaceMembers.value;
      const accountId = profile.value.user.accountId;
      if (!wsList?.length || !members) return false;
      return wsList.some((ws) => members[ws.id]?.includes(accountId));
    });

    return {
      workspaceName,
      errorInName,
      showMoreOptions,
      canCreateWorkspace,
      canRenameWorkspace,
      getInitials,
      handleCreateWorkspace,
      workspaces,
      defaultWorkspaceId,
      workspaceMembers,
      navigateToWorkspace,
      openWorkspace,
      renameWorkspace,
      RouteNames,
      deleteWorkspace,
      isEntitledToWorkspaces,
      belongsToAnyWorkspace,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss" as *;

.workspace-list {
  margin: 0 0 24px 0;
}

.workspace-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid;

  @include themed() {
    border-color: t($grey-10);
  }

  &:last-child {
    border-bottom: none;
  }
}

.workspace-info {
  display: flex;
  align-items: center;
  gap: 12px;

  &.workspace-button {
    background: none;
    border: none;
    text-align: start;
    flex: 1;
    padding: 0 12px;
    border-radius: 8px;
    transition: background-color 0.2s ease;

    @include themed() {
      color: inherit;
    }

    &:focus-visible {
      outline: 2px solid;
      outline-offset: 2px;
      @include themed() {
        outline-color: t($primary);
      }
    }
  }
}

.avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;

  &.placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 500;

    @include themed() {
      background-color: t($grey-10);
      color: t($grey-60);
    }
  }
}

.details {
  .name {
    @extend %font-body-medium;

    @include themed() {
      color: t($primary);
    }

    &--disabled {
      @include themed() {
        color: t($grey-50);
      }
    }
  }

  .members {
    @extend %font-caption-regular;

    @include themed() {
      color: t($grey-60);
    }

    &--disabled {
      @include themed() {
        color: t($grey-40);
      }
    }
  }
}

.status {
  display: flex;
  align-items: center;
  gap: 12px;
}

.create-section {
  margin-top: 32px;
}

.info-text {
  margin-bottom: 16px;

  @include themed() {
    color: t($grey-60);
  }
}

.create-input {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;

  :deep(.form-input) {
    flex: 1;
  }

  .base-button {
    flex-shrink: 0;
  }
}

.default-tag {
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 4px;
  margin-inline-start: 8px;

  @include themed() {
    background-color: t($grey-10);
    color: t($grey-60);
  }
}
</style>
